/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";
import validate from "../util/validate.js";
import googleMaps from "../util/google-maps.js";

var program = {
	code: "sop",
	name: "Sydney Olympic Park",
	description: "Sydney Olympic Park is located in the inner west of Sydney on the banks of the Parramatta River. Nearly half of the Park (304 hectares) is zoned for environmental conservation and management due to its high ecological values. Many species and ecological communities are present that were once widespread in Sydney but are now uncommon in urban areas, and some are now listed as threatened under State and/or Commonwealth environmental legislation. The Parks ecological monitoring program provides ongoing assessment of biodiversity status and threats, and to guide management response. Surveys under this program are available to registered users only.",
	canParticipate: function(user) {
		return _.any(user.permissions, function(p) {
			return p.type == "program" && p.program == "sop";
		});
	},
	canFilterBy: function(user) {
		return this.canParticipate(user);
	},
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = {
				sop_survey_type: lookups.sop_survey_type,
				detection_method: lookups.detection_method,
				activity: lookups.wa_cockies_activity,
				reptile_life_stage: lookups.life_stage.filter(l => ['adult', 'egg', 'juvenile', 'mixed'].includes(l.code)),
				mammal_life_stage: lookups.life_stage.filter(l => ['adult', 'juvenile', 'mixed'].includes(l.code)),
				amphibian_life_stage: lookups.life_stage,
				vital_status: lookups.vital_status,
				sex: lookups.sex
			};
			return true;
		});
	},
	programData: function(survey) {
		// Setup survey point validation
		var surveyPointError = ko.observable(null);
		ko.computed(function() {
			if(survey.program() == program && survey.surveyPoint()) {
				var sp = survey.surveyPoint();
				var areasPromise = birdata.areasAtPoint(sp.lat, sp.lon);
				var sharedSitesPromise = birdata.sharedSitesAtPoint(sp.lat, sp.lon);
				Promise.all([areasPromise, sharedSitesPromise]).then(function([areas, sharedSites]) {
					var all = _.flatten([areas, sharedSites]);

					if(_.any(all, function(x) { return x.layer && x.layer.substr(0,3) == "sop"; })) {
						return null;
					} else {
						return "Invalid location selected: Survey point must lie within a SOP Precinct or Quadrat.";
					}
				}).then(surveyPointError);
			} else {
				surveyPointError(null);
			}
		});

		var fields = program.fields();

		var allSpecies = ko.observable([]);
		birdata.speciesList({ supertaxa: 'all' }).then(allSpecies);

		// var amphibians = ko.observable([]);
		// birdata.speciesList({ supertaxa: 'class:Amphibia' }).then(amphibians);

		var birds = ko.observable([]);
		birdata.speciesList().then(birds);

		return {
			fields: fields,
			validations: [
				validate(ko.observable(), function() { return surveyPointError(); }, { tab: 'location' }),
				validate.field(fields.sopSurveyType, "Survey type").required().build()
			],
			surveyPointError: surveyPointError,
			speciesList: ko.pureComputed(function() {
				var type = fields.sopSurveyType() && fields.sopSurveyType().code;
				// if(type == "incidental") {
					return allSpecies(); // Note - important not to create a new observable here
				// } else {
					// return birds();
				// }
			})
		}
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var surveyTypes = _.filter(program.options.sop_survey_type, function(t) {
			return _.contains([
				"spring_bird_census",
				"lathams_snipe",
				"woodland_bird",
				"migratory_bird",
				"evening_waterbird",
				"red_rumped_parrot",
				"black_winged_stilt",
				"nocturnal_bird",
				"incidental",
				"fauna_general",
				"reptile_annual_census",
				"ggbf_monitoring",
				"fats_auditory",
				"microbat",
				"white_striped_free_tailed_bat"
			], t.code);
		});

		var fields = [
			fieldManager.field({
				name: "sopSurveyType",
				type: "options",
				label: "Survey Type",
				options: surveyTypes
			})
		];

		return util.arrayToMap(fields, function(f) { return f.field.name; });
	},
	sightingFields: function(sightingModel) {
		var species = sightingModel ? sightingModel.species : null;

		var fields = [
			{
				name: "detectionMethod",
				type: "options",
				label: "Detection Method",
				options: program.options.detection_method
			},
			{
				name: "activity",
				type: "options",
				label: "Behaviour",
				options: program.options.activity
			},
			{
				name: "lifeStage",
				type: "options",
				label: "Age / Life stage",
				options: species == null ? [] :
					species.supertaxa.includes('class:Amphibia') ? program.options.amphibian_life_stage :
					species.supertaxa.includes('class:Mammalia') ? program.options.mammal_life_stage :
					species.supertaxa.includes('class:Reptilia') ? program.options.reptile_life_stage : []
			},
			{
				name: "vitalStatus",
				type: "options",
				label: "Vital status",
				options: program.options.vital_status
			},
			{
				name: "sex",
				type: "options",
				label: "Sex",
				options: program.options.sex
			},
			{
				name: "weightInGrams",
				type: "number",
				label: "Weight",
				units: "g",
				validate: ">0,<1000000"

			},
			{
				name: "lengthInMillimetres",
				type: "number",
				label: "Length",
				units: "mm",
				validate: ">0,<10000"
			},
			{
				name: "legLengthInMillimetres",
				type: "number",
				label: "Leg length",
				units: "mm",
				validate: ">0,<10000"
			},
			{
				name: "snoutLengthInMillimetres",
				type: "number",
				label: "Snout/vent length",
				units: "mm",
				validate: ">0,<1000"
			},
			{
				name: "nuptialPads",
				type: "boolean",
				allowNull: true,
				label: "Nuptial pads present"
			},
			{
				name: "tagNumber",
				type: "text",
				label: "Tag number"
			},
			{
				name: "recapture",
				type: "boolean",
				allowNull: true,
				label: "Recapture"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		fields.detectionMethod(_.findWhere(program.options.detection_method, { code: 'seen' }));

		if(!species.supertaxa.includes("class:Aves")) {
			fields.vitalStatus(program.options.vital_status.find(l => l.code == 'live'));
		}

		validate.field(fields.detectionMethod, "Detection Method", { tab: 'sightings' }).required().build();

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().sop.fields;
		json.sop = {};
		_.each(fields, function(f) {
			f.field.save(json.sop);
		});
		delete json.survey_type;
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.sop;

		if(!data) return; //incomplete survey

		_.each(fields, function(f) {
			f.field.load(data);
		});
	},
	visibleSharedSiteLayers: function(survey) {
		var data = survey.programData().sop;
		var surveyType = data && data.fields.sopSurveyType();
		var code = surveyType && surveyType.code;

		var layer = {
			spring_bird_census: 'sop_sbc',
			woodland_bird: 'sop_woodland',
			fats_auditory: 'sop_frogpond',
			ggbf_monitoring: 'sop_frogpond',
			microbat: 'sop_anabat',
			reptile_annual_census: 'sop_reptile',
		}[code]

		return layer || 'none'
		// Nothing special about 'none' here - it just doesn't match any site layer. If we return null, it defaults to showing birdata shared
		// sites. TODO - allow returning of null to hide all sites and make programs have to explicity opt in to show birdata sites
	},
	defaultAreaLayer: function(layers) {
		return _.findWhere(layers, { code: "sop" });
	},
	updateMap: function(map, survey, areasPromise, locationViewModel) {
		// Zoom to SOP Area
		if(survey.program() == program && !survey.surveyPoint()) {
			areasPromise.then(function(areas) {
				var bounds;
				_.each(areas, function(area) {
					if(area.layer == "sop") {
						bounds = googleMaps.bounds(area.bounds, bounds);
					}
				});

				if(bounds) {
					var oldBounds = map.getBounds();

					var alreadyInsideBounds = oldBounds && bounds.contains(oldBounds.getSouthWest()) && bounds.contains(oldBounds.getNorthEast());

					if(!alreadyInsideBounds) {
						map.fitBounds(bounds);
					}
				}
			});
		}
	},
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'sop',
			name: '(Sydney Olympic Park)'
		});
	},
	multipleSightingsPerSpecies: true,
	requireSightingCount: true
};

export default program;
